class MoteurBhPredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('moteur-bh-predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.searchTermDataOnLoad;
    
    this.collectionSelect = this.getAttribute('data-collection-select');

    this.contextSelect = this.getAttribute('data-context-select');
    this.contextKeySelect = this.getAttribute('data-context-key-select');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  setupSearchTermDataOnLoad() {
    if (this.searchTermDataOnLoad == undefined) {
      const t = this.getQuery();
      this.searchTermDataOnLoad = (`${t}` != "");
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    this.setupSearchTermDataOnLoad();

    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.collectionSelect || this.contextSelect) {
      if (this.querySelector('[aria-selected="true"] a')) event.preventDefault();
      return;
    }
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    this.setupSearchTermDataOnLoad();
    
    super.onFormReset(event);
    
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
    if (this.searchTermDataOnLoad && this.collectionSelect || this.contextSelect) {
      this.input.form.submit();
    }
  }

  onFocus() {
    this.setupSearchTermDataOnLoad();

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const m = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (m && m.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    const _this = this;
    const paramsPush = {
        fieldsReturn: "url,vendor,title,productType,fk_auteur,fk_collection,price",
        search_collections: true,
        search_collections_limit: 5,
        search_auteurs: true,
        search_auteurs_limit: 5,
        order_by: 'pertinence',
        order_by_sens: 'desc',
        limit: 10,
        debug: false,
        delay_autocomplete:200
    };
    
    if (this.collectionSelect) {
      paramsPush['is_collection'] = true;
      paramsPush['collection'] = this.collectionSelect;

      paramsPush['search_collections'] = false;
      paramsPush['search_auteurs'] = false;

      if ($("#list_id_pdt_collection").length > 0) {
        const tabDataTmp = `${$("#list_id_pdt_collection").val()}`.trim().split('-');
        const tabIds = [];
        tabDataTmp.forEach(idPush => {
            const idTest = `${idPush}`.trim();
            if(idTest != "") {
                tabIds.push(idTest);
            }
        });
        paramsPush['ids'] = tabIds;
      }
    }
    if (this.contextSelect) {
      paramsPush.q_filter_m = {};
      paramsPush.q_filter_m[`${this.contextKeySelect}`] = [this.contextSelect];

      paramsPush['search_collections'] = false;
      paramsPush['search_auteurs'] = false;
    }

    bhmoteur.BHMoteurAutoCompleteSearch(
      queryKey, 
      paramsPush, 
      {
          idTagTemplate: "template_moteur_bh_predictive_search", // id script template nunjucks
          idTagTarget: function(resultsMarkup) {
            _this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            _this.renderSearchResults(resultsMarkup);
            _this.checkImgsExist();
          }, // id cible pour l'intertion du code
      }, 
      function(dataResult) {
          // console.log(dataResult);
      },
      function(onLoadingCallback) {
          // console.log('onLoadingCallback BHMoteurAutoCompleteSearch', onLoadingCallback);
      },
      function(err) {
          // console.log('onErrorCallback BHMoteurAutoCompleteSearch', err);
      } 
    );  
  }

  checkImgsExist() {
    $(this).find("img.predictive-search__image").each(function() {
        const _this = this;

        const img = new Image();
        img.src = $(this).attr("src");

        if (img.complete) {
            // on est OK
        } else {
            img.onload = () => {
                // on est OK
            };           
            img.onerror = () => {
                // PB, on met une image par defaut
                $(_this).attr("src", 'https://cdn.shopify.com/s/files/1/0517/5915/3314/files/noimage.png?v=1697224497');
                $(_this).attr("srcset", 'https://cdn.shopify.com/s/files/1/0517/5915/3314/files/noimage.png?v=1697224497');
            };
        }
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('moteur-bh-predictive-search', MoteurBhPredictiveSearch);
